import { useRef, useState } from 'preact/hooks';
import { ArrowLeft, Dices, GraduationCap, LogIn, Sparkles, UserRound } from 'lucide-preact';
import { audio } from '../../game/audio/Audio';
import { markTutorialDone, nameSuggestions } from '../state/actions';
import { generateName, nameProblem } from '../state/names';
import { storedBirthYear } from '../state/offline';
import { startTutorial } from '../state/play';
import { chooseName, continueAsGuest, finishOnboarding, login, onboardingNote, onboardingStep, submitBirthYear } from '../state/session';
import { conn, cosmetics, me } from '../state/store';
import { AsyncButton, Field } from '../ui/kit';
import { CharacterPreview } from '../three/CharacterPreview';
import { Logo } from './Boot';
import { openLegal } from './Legal';
import { ForgotPasswordLink } from './PasswordReset';

function YearPicker(props: { value: number | null; onChange: (y: number) => void }) {
  const now = new Date().getFullYear();
  const decades: number[] = [];
  for (let d = Math.floor(now / 10) * 10; d >= 1930; d -= 10) decades.push(d);
  const [decade, setDecade] = useState<number | null>(props.value !== null ? Math.floor(props.value / 10) * 10 : null);
  const years = decade === null ? [] : Array.from({ length: 10 }, (_, i) => decade + i).filter((y) => y <= now);
  return (
    <div class="yearpick">
      <div class="yearpick-row decades">
        {decades.map((d) => (
          <button
            key={d}
            class={`yearpick-btn ${decade === d ? 'is-on' : ''}`}
            onClick={() => {
              audio.play('ui_click');
              setDecade(d);
            }}
          >
            {d}s
          </button>
        ))}
      </div>
      {decade !== null && (
        <div class="yearpick-row years">
          {years.map((y) => (
            <button
              key={y}
              class={`yearpick-btn year ${props.value === y ? 'is-on' : ''}`}
              onClick={() => {
                audio.play('ui_click');
                props.onChange(y);
              }}
            >
              {y}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AgeStep() {
  const [year, setYear] = useState<number | null>(null);
  return (
    <div class="onb-card">
      <h1 class="onb-title">Welcome, recruit!</h1>
      <p class="onb-sub">What year were you born?</p>
      <YearPicker value={year} onChange={setYear} />
      <AsyncButton
        class="primary big onb-continue"
        disabled={year === null}
        onClick={async () => {
          audio.unlock();
          if (year !== null) await submitBirthYear(year);
        }}
      >
        Continue
      </AsyncButton>
      <div class="onb-links">
        <button class="linkish" onClick={() => (onboardingStep.value = 'login')}>
          <LogIn size={16} /> I already have an account
        </button>
      </div>
      <p class="onb-legal">
        By continuing you agree to our{' '}
        <button class="linkish" onClick={() => openLegal('terms')}>
          Terms
        </button>{' '}
        and{' '}
        <button class="linkish" onClick={() => openLegal('privacy')}>
          Privacy Policy
        </button>
        .
      </p>
    </div>
  );
}

function WelcomeStep() {
  return (
    <div class="onb-card">
      <h1 class="onb-title">Welcome back!</h1>
      <p class="onb-sub">How do you want to play?</p>
      <div class="onb-choices">
        <AsyncButton class="primary big" onClick={() => continueAsGuest(false)}>
          <UserRound size={22} /> Play as guest
        </AsyncButton>
        <button class="btn big" onClick={() => (onboardingStep.value = 'login')}>
          <LogIn size={22} /> Log in
        </button>
      </div>
      <p class="onb-legal">Guest progress is saved on this device. Link an email in Settings to keep it safe.</p>
    </div>
  );
}

function LoginStep() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const valid = /.+@.+\..+/.test(email) && pw.length >= 6;
  return (
    <div class="onb-card">
      <button class="onb-back" onClick={() => (onboardingStep.value = storedBirthYear() !== null ? 'welcome' : 'age')} aria-label="Back">
        <ArrowLeft size={22} />
      </button>
      <h1 class="onb-title">Log in</h1>
      <p class="onb-sub">Use the email you linked to your marine.</p>
      <form
        class="onb-form"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Field label="Email">
          <input class="ui-input" type="email" autoComplete="email" value={email} onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Password">
          <input class="ui-input" type="password" autoComplete="current-password" value={pw} onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <AsyncButton class="primary big onb-continue" disabled={!valid} onClick={() => login(email, pw)}>
          Log in
        </AsyncButton>
        <ForgotPasswordLink email={email} />
      </form>
    </div>
  );
}

/**
 * Call-sign suggestions: the server's generated names when online (restricted accounts may only
 * pick from those), a local generator offline.
 */
export function useNameRoller(): () => Promise<string> {
  const pool = useRef<string[]>([]);
  return async () => {
    if (conn.value === 'online') {
      if (!pool.current.length) pool.current = await nameSuggestions();
      const next = pool.current.shift();
      if (next) return next;
    }
    return generateName();
  };
}

function NameStep() {
  const m = me.value;
  const restrictedAcct = m?.restricted ?? false;
  const [name, setName] = useState(() => (restrictedAcct ? m?.name ?? generateName() : ''));
  const [touched, setTouched] = useState(false);
  const roll = useNameRoller();
  const problem = nameProblem(name);
  const next = (): void => {
    if (me.value?.tutorialDone) finishOnboarding();
    else onboardingStep.value = 'tutorial';
  };
  const c = cosmetics.value;
  return (
    <div class="onb-card onb-name">
      <div class="onb-name-hero">
        <CharacterPreview skin={c.skin} armor={c.armor} weapon="brick_rifle" rotate className="onb-hero-3d" />
      </div>
      <div class="onb-name-form">
        <h1 class="onb-title">{restrictedAcct ? "Here's your call sign!" : 'Pick your call sign'}</h1>
        {restrictedAcct ? (
          <>
            <div class="nameplate">{name}</div>
            <AsyncButton
              onClick={async () => {
                audio.play('ui_click');
                setName(await roll());
              }}
            >
              <Dices size={20} /> Roll another
            </AsyncButton>
            <p class="onb-legal">To keep everyone safe, call signs are picked for you.</p>
          </>
        ) : (
          <>
            <Field label="Call sign" error={touched ? problem : null} hint="3–16 letters, numbers or underscores.">
              <div class="ui-input-row">
                <input
                  class="ui-input big"
                  maxLength={16}
                  value={name}
                  placeholder="e.g. BrickBreaker"
                  autoComplete="off"
                  spellcheck={false}
                  onInput={(e) => {
                    setName((e.currentTarget as HTMLInputElement).value.replace(/\s/g, ''));
                    setTouched(true);
                  }}
                />
                <AsyncButton
                  class="icon"
                  title="Suggest a name"
                  aria-label="Suggest a name"
                  onClick={async () => {
                    audio.play('ui_click');
                    setName(await roll());
                    setTouched(true);
                  }}
                >
                  <Dices size={22} />
                </AsyncButton>
              </div>
            </Field>
            <p class="onb-legal">Don't use your real name or anything personal.</p>
          </>
        )}
        {onboardingNote.value && <div class="onb-error">{onboardingNote.value}</div>}
        <AsyncButton
          class="primary big onb-continue"
          disabled={!!problem}
          onClick={async () => {
            onboardingNote.value = null;
            setTouched(true);
            if (problem) return;
            if (await chooseName(name)) {
              audio.play('ui_buy');
              next();
            }
          }}
        >
          {restrictedAcct ? 'Use this name' : 'Continue'}
        </AsyncButton>
      </div>
    </div>
  );
}

function TutorialStep() {
  return (
    <div class="onb-card onb-tutorial">
      <div class="onb-tut-icon">
        <GraduationCap size={56} />
      </div>
      <h1 class="onb-title">Boot Camp</h1>
      <p class="onb-sub">Learn to move, shoot, build and blow things up in a few minutes. No other players, no pressure.</p>
      <div class="onb-choices">
        <button
          class="btn primary big"
          onClick={() => {
            audio.unlock();
            finishOnboarding();
            startTutorial();
          }}
        >
          <Sparkles size={22} /> Start Boot Camp
        </button>
        <button
          class="btn big"
          onClick={() => {
            audio.play('ui_click');
            finishOnboarding();
            void markTutorialDone();
          }}
        >
          Skip for now
        </button>
      </div>
      <p class="onb-legal">You can replay Boot Camp any time from Play → Practice.</p>
    </div>
  );
}

export function Onboarding() {
  const step = onboardingStep.value;
  return (
    <div class="onb">
      <div class="onb-bg" aria-hidden="true">
        {Array.from({ length: 14 }, (_, i) => (
          <i key={i} class="floaty" style={{ left: `${(i * 37) % 100}%`, animationDelay: `${-i * 1.7}s`, animationDuration: `${14 + (i % 5) * 3}s` }} />
        ))}
      </div>
      <div class="onb-logo">
        <Logo size="md" />
      </div>
      {conn.value === 'offline' && step !== 'age' && <div class="onb-offline">Offline mode — progress stays on this device until you reconnect.</div>}
      <div class="onb-stage" key={step}>
        {step === 'age' && <AgeStep />}
        {step === 'welcome' && <WelcomeStep />}
        {step === 'login' && <LoginStep />}
        {step === 'name' && <NameStep />}
        {step === 'tutorial' && <TutorialStep />}
      </div>
      {step === 'welcome' && onboardingNote.value && <div class="onb-note">{onboardingNote.value}</div>}
    </div>
  );
}
